import { parseAndMergeAndConvert } from '../../common/utils';
import { generateObjectPageSchemaV2 } from './objectPage';
// Normally generateListReportSchemaV2 will be routed by pageAccess to the sibling file listReport.ts of this file.
// For manual tests this can be changed by switching useGenericSchemaHandling in pageAccess.ts to true.
// In this case generic schema handling and the schema for ListReportNew will be used.
import { generateListReportSchemaV2 } from '../genericSchemaHandling/pages/pageAccess';
import { generateAnalyticalListReportSchemaV2 } from './analyticalListReport';
import { generateOverviewPageSchemaV2 } from './overviewPage';
import type { ExtensionLogger, GenerateAppSchemaParameters, FileData, Manifest } from '@sap/ux-specification-types';
import { LogSeverity, FioriElementsVersion, TemplateType } from '@sap/ux-specification-types';
import { getEntitySetByEntitySetName } from '../../common/importProject';
import i18next from 'i18next';
import { log } from '../../../extensionLogger';
import { initI18n } from '../../../i18n/i18n';

/**
 * Common logic for LR and OP to prepare the generation.
 *
 * @param {FileData[]} annotations - list of all annotation files (file content plus URI as identifier)
 * @param {ExtensionLogger} logger - Logger class for logging messages
 * @param {object} manifest - manifest.json of the app
 * @param {FileData[]} [fragments] - Array with XML fragments.
 * @param {string} entitySet - the base entity set name of the given page
 * @param {string[]} flex - list of flex changes to be considered
 * @param templateType - the type of the template used for schema generation
 * @returns the filled interface structure with all information for the schema generation
 */
function prepareGenerationV2(
    annotations: FileData[],
    logger: ExtensionLogger,
    manifest: Manifest,
    fragments: FileData[],
    entitySet: string,
    flex: string[],
    templateType: TemplateType
): GenerateAppSchemaParameters {
    initI18n();

    const serviceAVT = parseAndMergeAndConvert(annotations, logger);
    const generateParameters: GenerateAppSchemaParameters = {
        templateType,
        fioriElementsVersion: FioriElementsVersion.v2,
        manifest,
        fragments,
        serviceAVT,
        flex,
        logger
    };
    generateParameters.entitySet = getEntitySetByEntitySetName(entitySet, generateParameters.serviceAVT);
    if (!generateParameters.entitySet) {
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('ENTITYSETNOTFOUND', { entitySetName: entitySet })
        });
    }
    return generateParameters;
}

/**
 * Generates an app specific schema out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {object} genericSchema - generic JSON schema of an object page
 * @param {string} entitySet - the base entity set name of the given page
 * @param {FileData[]} annotations - list of all annotation files (file content plus URI as identifier)
 * @param {object} manifest - manifest.json of the app
 * @param {FileData[]} [fragments] - Array with XML fragments.
 * @param {string[]} flex - list of flex changes to be considered
 * @param {ExtensionLogger} logger - Logger class for logging messages
 * @returns the app specific JSON schema
 */
export function generateObjectPageInterfaceV2(
    genericSchema: object,
    entitySet: string,
    annotations: FileData[],
    manifest: Manifest,
    fragments?: FileData[],
    flex?: string[],
    logger?: ExtensionLogger
): object {
    const generateParameters = prepareGenerationV2(
        annotations,
        logger,
        manifest,
        fragments,
        entitySet,
        flex,
        TemplateType.ListReportObjectPageV2
    );
    return generateObjectPageSchemaV2(generateParameters, genericSchema);
}

/**
 * Generates the app specific schema of a list report out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param genericSchema - generic JSON schema of a list report
 * @param entitySet - the base entity set name of the given page
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param manifest - manifest.json of the app
 * @param {FileData[]} fragments - list of fragment files and their content
 * @param {string[]} flex - list of flex changes to be considered
 * @param {ExtensionLogger} logger - Logger class for logging messages
 * @returns the app specific JSON schema
 */
export function generateListReportInterfaceV2(
    genericSchema: object,
    entitySet: string,
    annotations: FileData[],
    manifest: Manifest,
    fragments?: FileData[],
    flex?: string[],
    logger?: ExtensionLogger
): object {
    const generateParameters = prepareGenerationV2(
        annotations,
        logger,
        manifest,
        fragments,
        entitySet,
        flex,
        TemplateType.ListReportObjectPageV2
    );
    return generateListReportSchemaV2(generateParameters, genericSchema);
}

/**
 * Generates the app specific schema of an analytical list page out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param genericSchema - generic JSON schema of a list report
 * @param entitySet - the base entity set name of the given page
 * @param annotations - list of all annotation files (file content plus URI as identifier)
 * @param manifest - manifest.json of the app
 * @param fragments - list of fragment files and their content
 * @param {string[]} flex - list of flex changes to be considered
 * @param logger - Logger class for logging messages
 * @returns the app specific JSON schema
 */
export function generateAnalyticalListPageInterfaceV2(
    genericSchema: object,
    entitySet: string,
    annotations: FileData[],
    manifest: Manifest,
    fragments?: FileData[],
    flex?: string[],
    logger?: ExtensionLogger
): object {
    const generateParameters = prepareGenerationV2(
        annotations,
        logger,
        manifest,
        fragments,
        entitySet,
        flex,
        TemplateType.AnalyticalListPageV2
    );
    return generateAnalyticalListReportSchemaV2(generateParameters, genericSchema);
}

/**
 * Generates the app specific schema of an overview page.
 *
 * @param genericSchema - file content of the generic schema
 * @param manifest - manifest.json of the app
 * @returns the generic schema, as equivalent to the app schema
 */
export function generateOVPInterfaceV2(genericSchema: object, manifest: Manifest): object {
    return generateOverviewPageSchemaV2(genericSchema, manifest);
}
